"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/features/auth/hook/useAuth";
import { AuthError } from "@/components/shared/AuthError";
import { HR_ROLES, hasAnyRole } from "@/lib/rbac";

// Mirrors middleware.ts's ADMIN_ROLES exactly — kept local (not exported
// from rbac.ts) since it's an ecommerce-admin-only concept, same as before.
const ADMIN_ROLES = ["ADMIN", "SUPERADMIN", "VENDOR"];

const loginSchema = z.object({
    email: z.string()
        .email("Email invalide")
        .min(1, "Email obligatoire"),
    password: z.string()
        .min(8, "Minimum 8 caractères")
        .min(1, "Mot de passe obligatoire"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const LoginForm = () => {
    const router = useRouter();
    const { login, isLoading, error } = useAuth();
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = (values: LoginFormValues) => {
        login(values, {
            onSuccess: (response) => {
                const user = response.data.user;

                // HR-only roles (Comptable, Gérant, ...) land directly in the
                // HR workspace — mirrors middleware.ts's already-correct
                // redirect logic for the "revisit /auth/login while
                // authenticated" path, previously missing here.
                if (ADMIN_ROLES.includes(user.role)) {
                    router.push("/admin/dashboard");
                } else if (hasAnyRole(user.role, HR_ROLES)) {
                    router.push("/admin/hr/dashboard");
                } else {
                    router.push("/");
                }
            },
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

            {/* Email */}
            <div>
                <label className="text-sm text-primary-800">
                    Email ou Numéro de téléphone
                </label>
                <Input
                    {...register("email")}
                    className="mt-2"
                />
                {errors.email && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.email.message}
                    </p>
                )}
            </div>

            {/* Password */}
            <div className="relative">
                <label className="text-sm text-primary-800">
                    Mot de passe
                </label>

                <Input
                    {...register("password")}
                    type={showPassword ? "text" : "password"}
                    className="mt-2 pr-10"
                />

                <button
                    type="button"
                    className="absolute right-3 top-9.5 text-gray-400"
                    onClick={() => setShowPassword(!showPassword)}
                >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>

                {errors.password && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.password.message}
                    </p>
                )}
            </div>

            <AuthError message={error as string} />

            <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? "Connexion..." : "Se connecter"}
            </Button>

            <p className="text-center text-sm text-gray-500">
                Vous n&apos;avez pas de compte ?{" "}
                <Link href="/auth/register" className="text-primary-600 font-medium">
                    S&apos;inscrire
                </Link>
            </p>
        </form>
    );
};

